/**
 * OKS-48 — XP number formatting helpers.
 *
 * Two functions:
 *
 *   - `formatXpNumber(n, locale)` — locale-aware thousand grouping
 *     (Intl.NumberFormat). Used by the creator profile big-number,
 *     the brand roster chip, and the brand creator-detail tile.
 *
 *   - `formatXpKSuffix(n, locale)` — collapses to a `K`-suffix at
 *     ≥1_000 for the creator-app header pill on tight viewports
 *     (UX Spec §5 — 320px breakpoint). `1_247 XP` → `1.2K XP`,
 *     `12_345 XP` → `12K XP`, `1_234_567 XP` → `1.2M XP`. Below
 *     1_000, returns the unsuffixed number (no need to compress).
 *
 * Both are pure functions; no external dependencies beyond
 * `Intl.NumberFormat` (browser builtin, polyfilled in jsdom for
 * Vitest).
 */

const safeLocale = (locale: string | undefined): string => locale ?? "fr"

/**
 * Locale-aware thousand grouping. `1247` → `"1,247"` (en) or `"1 247"` (fr).
 *
 * @param n      The XP balance (non-negative integer).
 * @param locale BCP-47 tag, defaults to `"fr"` (Crew default).
 */
export function formatXpNumber(n: number, locale?: string): string {
  return new Intl.NumberFormat(safeLocale(locale)).format(n)
}

/**
 * `K` / `M` suffix variant for the header pill on tight viewports
 * (UX Spec §5).
 *
 * Rules:
 *   - n < 1_000          → `formatXpNumber(n, locale)` (no suffix)
 *   - 1_000 ≤ n < 10_000 → one decimal, e.g. `"1.2K"` (locale-aware
 *                          decimal separator via `Intl.NumberFormat`)
 *   - 10_000 ≤ n < 1M    → integer K, e.g. `"12K"`
 *   - n ≥ 1_000_000      → one decimal M, e.g. `"1.2M"`
 *
 * Always non-negative — the caller is responsible for clamping
 * (XpEvent.points has a CHECK > 0 floor + the SUM is always ≥ 0).
 */
export function formatXpKSuffix(n: number, locale?: string): string {
  if (n < 1_000) return formatXpNumber(n, locale)

  const fmt = (value: number, fractionDigits: number): string =>
    new Intl.NumberFormat(safeLocale(locale), {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)

  if (n < 10_000) return `${fmt(n / 1_000, 1)}K`
  if (n < 1_000_000) return `${fmt(Math.floor(n / 1_000), 0)}K`
  return `${fmt(n / 1_000_000, 1)}M`
}
